import React, { useMemo, useState } from 'react';
import {
  askAiAssistantWithPreparedPrompt,
  buildAiAssistantPromptPreview,
  ChatMessage,
  OpenAiPreparedRequest,
  sendAiAssistantMessages,
  type AiAssistantEmployee,
} from '../../services/aiAssistantService';
import { buildAiAssistantEmployees } from '../../utils/aiAssistantEmployeeContext';
import { AI_ASSISTANT_DEV_PREVIEW_ENABLED } from '../../config/featureFlags';
import { AiAssistantAccessInfo, isQuotaLimitedAiMode, mergeTrialInfo } from '../../services/aiAssistantAccessService';
import AccessDeniedModal from './AccessDeniedModal';

const QUICK_PROMPTS = [
  'Сравни структуру оплаты Ассоциата и Инвестиционного Директора',
  'Какие позиции у меня наиболее недооплачены относительно рынка?',
  'Какую зарплату стоит предложить опытному Ассоциату, чтобы привлечь сильного кандидата?',
];

const MODEL_PRICING_PER_1M_TOKENS: Record<string, { input: number; output: number }> = {
  'gpt-4.1-mini': { input: 0.4, output: 1.6 },
};

export const AI_ASSISTANT_INITIAL_MESSAGES: ChatMessage[] = [
  {
    role: 'assistant',
    content:
      'Привет! Я AI-ассистент datacircle. Задай вопрос по компенсациям, и я отвечу на базе доступных тебе пакетов и данных сотрудников из локального кэша.',
  },
];

export type AiAssistantMeta = {
  accessiblePackages: string[];
  employeeCount: number;
  gradingTreeRoleCount?: number;
  employeesWithGrading?: number;
} | null;

type AiAssistantChatProps = {
  mode?: 'page' | 'widget';
  accessInfo?: AiAssistantAccessInfo;
  onAccessInfoChange?: (info: AiAssistantAccessInfo) => void;
  accessLoading?: boolean;
  sharedMessages?: ChatMessage[];
  onSharedMessagesChange?: (messages: ChatMessage[]) => void;
  sharedMeta?: AiAssistantMeta;
  onSharedMetaChange?: (meta: AiAssistantMeta) => void;
};

const AiAssistantChat: React.FC<AiAssistantChatProps> = ({
  mode = 'page',
  accessInfo,
  onAccessInfoChange,
  accessLoading = false,
  sharedMessages,
  onSharedMessagesChange,
  sharedMeta,
  onSharedMetaChange,
}) => {
  const isWidget = mode === 'widget';
  const hasAccess = Boolean(accessInfo?.hasAccess);

  const [localMessages, setLocalMessages] = useState<ChatMessage[]>(AI_ASSISTANT_INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [preparingPreview, setPreparingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [localMeta, setLocalMeta] = useState<AiAssistantMeta>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<OpenAiPreparedRequest | null>(null);
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[] | null>(null);
  const [previewMode, setPreviewMode] = useState<'readable' | 'raw'>('readable');
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const messages = sharedMessages ?? localMessages;
  const setMessages = onSharedMessagesChange ?? setLocalMessages;
  const meta = sharedMeta ?? localMeta;
  const setMeta = onSharedMetaChange ?? setLocalMeta;

  const hasConversation = messages.some((message) => message.role === 'user');
  const trialExhausted =
    !hasAccess &&
    Boolean(accessInfo?.trialUsed && accessInfo.trialUsed >= (accessInfo.trialLimit || 3));
  const showFullLockedScreen = !hasAccess && !hasConversation;
  const inputLocked = !hasAccess;

  const canSend = useMemo(
    () => !inputLocked && input.trim().length > 0 && !sending && !preparingPreview && !accessLoading,
    [inputLocked, input, sending, preparingPreview, accessLoading],
  );

  const executeSend = async (
    nextMessages: ChatMessage[],
    employees: AiAssistantEmployee[],
    preview?: OpenAiPreparedRequest,
  ) => {
    setSending(true);
    setError('');
    setMessages(nextMessages);
    setInput('');

    try {
      const response = preview
        ? await askAiAssistantWithPreparedPrompt(preview, employees)
        : await sendAiAssistantMessages(nextMessages, employees);
      const finalMessages = [...nextMessages, { role: 'assistant' as const, content: response.answer }];
      setMessages(finalMessages);
      setMeta(response.meta ?? null);
      if (response.trial && accessInfo && onAccessInfoChange) {
        onAccessInfoChange(mergeTrialInfo(accessInfo, response.trial));
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не удалось получить ответ. Попробуйте снова.';
      setError(message);
    } finally {
      setSending(false);
      setPendingPrompt(null);
      setPendingMessages(null);
    }
  };

  const sendMessage = async (content: string) => {
    const text = content.trim();
    if (!text || sending || preparingPreview || inputLocked) return;

    setError('');
    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setPreparingPreview(true);

    try {
      const employees = await buildAiAssistantEmployees();

      if (AI_ASSISTANT_DEV_PREVIEW_ENABLED) {
        const preview = await buildAiAssistantPromptPreview(nextMessages, employees);
        setPendingMessages(nextMessages);
        setPendingPrompt(preview);
        setPreviewMode('readable');
        setIsPreviewOpen(true);
      } else {
        await executeSend(nextMessages, employees);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не удалось получить ответ. Попробуйте снова.';
      setError(message);
    } finally {
      setPreparingPreview(false);
    }
  };

  const confirmSend = async () => {
    if (!pendingPrompt || !pendingMessages || sending) return;

    setIsPreviewOpen(false);
    const employees = await buildAiAssistantEmployees();
    await executeSend(pendingMessages, employees, pendingPrompt);
  };

  const cancelSend = () => {
    setIsPreviewOpen(false);
    setPendingPrompt(null);
    setPendingMessages(null);
  };

  const previewPayload = pendingPrompt
    ? {
        endpoint: 'https://api.openai.com/v1/chat/completions',
        body: {
          model: pendingPrompt.model,
          temperature: pendingPrompt.temperature,
          messages: pendingPrompt.messages,
        },
      }
    : null;

  const previewPayloadJson = previewPayload ? JSON.stringify(previewPayload, null, 2) : '';
  const readablePrompt = pendingPrompt
    ? pendingPrompt.messages
      .map((msg, index) => `#${index + 1} [${msg.role.toUpperCase()}]\n${msg.content}`)
      .join('\n\n────────────────────────────────────────\n\n')
    : '';
  const promptChars = previewPayloadJson.length;
  const estimatedInputTokens = Math.ceil(promptChars / 4);
  const estimatedOutputTokens = Math.max(200, Math.min(1200, Math.round(estimatedInputTokens * 0.35)));
  const pricing = pendingPrompt ? MODEL_PRICING_PER_1M_TOKENS[pendingPrompt.model] : undefined;
  const estimatedCostUsd = pricing
    ? (estimatedInputTokens / 1_000_000) * pricing.input + (estimatedOutputTokens / 1_000_000) * pricing.output
    : null;

  if (accessLoading) {
    return (
      <div style={{ padding: isWidget ? '0' : '24px', color: '#666', fontSize: '14px' }}>
        Проверяем доступ к AI-ассистенту…
      </div>
    );
  }

  if (showFullLockedScreen) {
    return (
      <div style={{ padding: isWidget ? '0' : '24px' }}>
        {!isWidget && (
          <>
            <h1
              style={{
                fontSize: '26px',
                fontWeight: '700',
                color: '#1a237e',
                margin: '0 0 6px 0',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              AI-ассистент
            </h1>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 20px 0' }}>
              Интеллектуальный помощник по компенсациям и рыночным данным.
            </p>
          </>
        )}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: isWidget ? '20px 16px' : '28px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e1e5e9',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-lock" style={{ fontSize: '20px', color: '#e65100' }} aria-hidden="true" />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h3
                style={{
                  fontSize: isWidget ? '16px' : '20px',
                  fontWeight: '600',
                  color: '#1a237e',
                  margin: '0 0 8px 0',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                AI-ассистент недоступен
              </h3>
              <p style={{ fontSize: '14px', color: '#555', margin: '0 0 16px 0', lineHeight: 1.6 }}>
                {accessInfo?.trialUsed && accessInfo.trialUsed >= accessInfo.trialLimit
                  ? 'Пробные запросы к AI-ассистенту исчерпаны. Оформите подписку или запросите презентацию — мы подключим полный доступ для вашего аккаунта.'
                  : 'Функция доступна по активной подписке datacircle. Оформите подписку или запросите презентацию — мы подключим доступ к ассистенту для вашего аккаунта.'}
              </p>
              <button
                type="button"
                onClick={() => setContactModalOpen(true)}
                style={{
                  padding: '10px 22px',
                  background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Запросить презентацию
              </button>
            </div>
          </div>
        </div>
        <AccessDeniedModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          packageName="AI-ассистент"
          context="AI-ассистент"
        />
      </div>
    );
  }

  return (
    <div style={{ padding: isWidget ? '0' : '24px' }}>
      {!isWidget && (
        <>
          <h1
            style={{
              fontSize: '26px',
              fontWeight: '700',
              color: '#1a237e',
              margin: '0 0 6px 0',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            AI-ассистент
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: '0 0 20px 0' }}>
            Ответы строятся по данным, доступным вашему аккаунту, и локальным данным сотрудников.
          </p>
        </>
      )}

      {isQuotaLimitedAiMode(accessInfo?.mode) && hasAccess && (
        <div
          style={{
            background: '#eef1fb',
            border: '1px solid #c5cae9',
            borderRadius: '10px',
            padding: '12px 14px',
            fontSize: '13px',
            color: '#232e8b',
            marginBottom: isWidget ? '12px' : '16px',
          }}
        >
          <i
            className={`fas ${accessInfo?.mode === 'limited' ? 'fa-robot' : 'fa-flask'}`}
            style={{ marginRight: '8px' }}
            aria-hidden="true"
          />
          {accessInfo?.mode === 'limited' ? (
            <>
              По подписке: осталось <strong>{accessInfo?.trialRemaining ?? 0}</strong> из{' '}
              <strong>{accessInfo?.trialLimit ?? 0}</strong> запросов.
            </>
          ) : (
            <>
              Пробный режим: осталось <strong>{accessInfo?.trialRemaining ?? 0}</strong> из{' '}
              <strong>{accessInfo?.trialLimit ?? 0}</strong> запросов.
            </>
          )}
        </div>
      )}

      {inputLocked && hasConversation && (
        <div
          style={{
            background: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '10px',
            padding: '12px 14px',
            fontSize: '13px',
            color: '#5d4037',
            marginBottom: isWidget ? '12px' : '16px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: '220px' }}>
            <i className="fas fa-lock" style={{ marginRight: '8px' }} aria-hidden="true" />
            {trialExhausted
              ? 'Пробные запросы исчерпаны. История чата сохранена — новые сообщения недоступны.'
              : 'Отправка сообщений недоступна для вашего аккаунта. История чата сохранена.'}
          </div>
          <button
            type="button"
            onClick={() => setContactModalOpen(true)}
            style={{
              padding: '8px 14px',
              background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Запросить презентацию
          </button>
        </div>
      )}

      {AI_ASSISTANT_DEV_PREVIEW_ENABLED && (
        <div
          style={{
            background: '#f8f9ff',
            border: '1px solid #d9deff',
            borderRadius: '10px',
            padding: '12px 14px',
            fontSize: '12px',
            color: '#3f4a83',
            marginBottom: '16px',
          }}
        >
          <i className="fas fa-shield-alt" style={{ marginRight: '6px' }} aria-hidden="true" />
          Dev-режим: перед отправкой открывается оверлей с полным prompt/payload для OpenAI.
        </div>
      )}

      <div data-tour="ai-prompts" style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap', marginBottom: '14px' }}>
        {QUICK_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => sendMessage(prompt)}
            disabled={inputLocked || sending || preparingPreview}
            style={{
              border: '1px solid #c5cae9',
              borderRadius: '20px',
              background: 'white',
              color: '#1a237e',
              padding: '8px 12px',
              fontSize: '12px',
              lineHeight: 1.35,
              cursor: inputLocked || sending || preparingPreview ? 'not-allowed' : 'pointer',
              opacity: inputLocked || sending || preparingPreview ? 0.55 : 1,
              flex: '1 1 0',
              minWidth: 0,
              whiteSpace: 'normal',
              textAlign: 'center',
            }}
          >
            {prompt}
          </button>
        ))}
      </div>

      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #e1e5e9',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          padding: '16px',
          minHeight: isWidget ? '420px' : '420px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!isWidget && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button
              type="button"
              onClick={() => {
                setMessages(AI_ASSISTANT_INITIAL_MESSAGES);
                setMeta(null);
                setError('');
                setInput('');
              }}
              style={{
                border: '1px solid #d0d5dd',
                borderRadius: '8px',
                background: 'white',
                color: '#475467',
                fontSize: '12px',
                fontWeight: 500,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              Очистить чат
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginBottom: '14px' }}>
          {messages.map((msg, idx) => (
            <div
              key={`${msg.role}-${idx}`}
              style={{
                marginBottom: '10px',
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '80%',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  background: msg.role === 'user' ? '#1a237e' : '#f2f4ff',
                  color: msg.role === 'user' ? '#fff' : '#1f2433',
                }}
              >
                {msg.content}
              </div>
            </div>
          ))}
          {(sending || preparingPreview) && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} aria-hidden="true" />
              {preparingPreview && AI_ASSISTANT_DEV_PREVIEW_ENABLED
                ? 'Формируем полный prompt для предпросмотра...'
                : 'Ассистент анализирует данные...'}
            </div>
          )}
        </div>

        {meta && (
          <div
            style={{
              marginBottom: '12px',
              borderRadius: '8px',
              background: '#f7f8fc',
              border: '1px solid #e7e9f3',
              padding: '10px 12px',
              fontSize: '12px',
              color: '#4f5670',
            }}
          >
            Источники: пакетов с доступом — {meta.accessiblePackages.length}, локальных сотрудников — {meta.employeeCount}
            {meta.gradingTreeRoleCount != null && (
              <>, ролей в grading tree — {meta.gradingTreeRoleCount}, с грейдом — {meta.employeesWithGrading ?? 0}</>
            )}
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: '12px',
              borderRadius: '8px',
              background: '#ffebee',
              border: '1px solid #ef9a9a',
              color: '#b71c1c',
              padding: '10px 12px',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!inputLocked) sendMessage(input);
          }}
          style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}
        >
          {inputLocked && (
            <div
              style={{
                fontSize: '13px',
                color: '#64748b',
                padding: '10px 12px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              Чтобы продолжить диалог, оформите подписку или запросите презентацию.
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                inputLocked
                  ? 'Пробные запросы исчерпаны'
                  : 'Например: где у меня наибольший гэп к 50p по FIX?'
              }
              disabled={inputLocked}
              style={{
                flex: 1,
                border: '1px solid #cfd6e4',
                borderRadius: '8px',
                padding: '11px 12px',
                fontSize: '14px',
                outline: 'none',
                background: inputLocked ? '#f8fafc' : '#fff',
                color: inputLocked ? '#94a3b8' : '#111',
                cursor: inputLocked ? 'not-allowed' : 'text',
              }}
            />
            <button
              type="submit"
              disabled={!canSend}
              style={{
                border: 'none',
                borderRadius: '8px',
                padding: '0 18px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#fff',
                background: canSend ? 'linear-gradient(135deg, #1a237e 0%, #283593 100%)' : '#9fa8da',
                cursor: canSend ? 'pointer' : 'not-allowed',
              }}
            >
              Отправить
            </button>
          </div>
        </form>
      </div>

      {AI_ASSISTANT_DEV_PREVIEW_ENABLED && isPreviewOpen && pendingPrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400,
            padding: '20px',
          }}
        >
          <div
            style={{
              width: 'min(980px, 95vw)',
              maxHeight: '88vh',
              overflow: 'hidden',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 14px 40px rgba(0,0,0,0.25)',
              border: '1px solid #e1e5e9',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #edf0f5' }}>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#1a237e', fontWeight: 600 }}>
                Предпросмотр полного prompt
              </h3>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#667085' }}>
                Проверь payload, который будет отправлен в OpenAI.
              </p>
            </div>

            <div style={{ padding: '14px 18px', overflow: 'auto', flex: 1 }}>
              <div
                style={{
                  marginBottom: '10px',
                  borderRadius: '8px',
                  background: '#f8f9ff',
                  border: '1px solid #d9deff',
                  padding: '10px 12px',
                  fontSize: '12px',
                  color: '#3f4a83',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '8px 14px',
                }}
              >
                <div>
                  <strong>Размер prompt:</strong> {promptChars.toLocaleString('ru-RU')} символов
                </div>
                <div>
                  <strong>Оценка input:</strong> ~{estimatedInputTokens.toLocaleString('ru-RU')} токенов
                </div>
                <div>
                  <strong>Оценка output:</strong> ~{estimatedOutputTokens.toLocaleString('ru-RU')} токенов
                </div>
                <div>
                  <strong>Стоимость:</strong>{' '}
                  {estimatedCostUsd !== null ? `~$${estimatedCostUsd.toFixed(4)}` : 'н/д для этой модели'}
                </div>
                <div style={{ gridColumn: '1 / -1', color: '#5c6ac4' }}>
                  Dev-оценка: токены считаются приближенно (chars/4), итог в биллинге может отличаться.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <button
                  type="button"
                  onClick={() => setPreviewMode('readable')}
                  style={{
                    border: '1px solid #c5cae9',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    background: previewMode === 'readable' ? '#e8eaf6' : 'white',
                    color: '#1a237e',
                    fontWeight: previewMode === 'readable' ? 600 : 500,
                  }}
                >
                  Читаемый prompt
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode('raw')}
                  style={{
                    border: '1px solid #c5cae9',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    background: previewMode === 'raw' ? '#e8eaf6' : 'white',
                    color: '#1a237e',
                    fontWeight: previewMode === 'raw' ? 600 : 500,
                  }}
                >
                  Raw JSON
                </button>
              </div>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '12px',
                  lineHeight: 1.45,
                  background: '#0f172a',
                  color: '#e2e8f0',
                  padding: '14px',
                  borderRadius: '8px',
                }}
              >
                {previewMode === 'readable' ? readablePrompt : previewPayloadJson}
              </pre>
            </div>

            <div
              style={{
                padding: '14px 18px',
                borderTop: '1px solid #edf0f5',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
              }}
            >
              <button
                type="button"
                onClick={cancelSend}
                style={{
                  border: '1px solid #d0d5dd',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#344054',
                  fontSize: '14px',
                  padding: '8px 18px',
                  cursor: 'pointer',
                }}
              >
                Нет
              </button>
              <button
                type="button"
                onClick={confirmSend}
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '8px 18px',
                  cursor: 'pointer',
                }}
              >
                Да
              </button>
            </div>
          </div>
        </div>
      )}

      <AccessDeniedModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        packageName="AI-ассистент"
        context="AI-ассистент"
      />
    </div>
  );
};

export default AiAssistantChat;
